import { LocaleKey, RuntimeLocaleKey, useFormatMessage } from "../../intl";
import { array, option, taskEither } from "fp-ts";
import { constTrue, constVoid, pipe } from "fp-ts/function";
import { Option } from "fp-ts/Option";
import {
  Box,
  ErrorBanner,
  Loader,
  LoadingButton,
  Space,
  Stack,
  useForm,
  useIsMobileLayout,
  validators,
} from "@dbn/omnichannel-design-system-g4p";
import {
  ExternalLoanItem,
  ExternalLoansListRework,
  loanProviders,
  loanProviderSelected,
  LoanType,
  RepaidLoansList,
} from "../api";
import { RepaidLiabilityFormModel } from "../domain";
import { useValidators } from "../../Common/useValidators";
import { foldTenant, MoneyAmount } from "../../globalDomain";
import { useCommand, useQuery } from "../../useAPI";
import * as remoteData from "../../RemoteData";
import { NonEmptyString } from "io-ts-types/lib/NonEmptyString";
import { useAppContext } from "../../useAppContext";
import { useState } from "react";
import { ReaderTaskEither } from "fp-ts/ReaderTaskEither";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { RepaidLiabilitiesEdititableItem } from "./RepaidLiabilitiesEditableItem";

export type CreditorConfig = {
  accountPlaceholder: LocaleKey;
  variableSymbolPlaceholder: Option<LocaleKey>;
  accountForDisbursment: Option<NonEmptyString>;
  accountReadonly: boolean;
};

type Props = {
  liabilities: ExternalLoanItem[];
  onSubmit: ReaderTaskEither<RepaidLoansList, unknown, unknown>;
  oldValues: Option<ExternalLoansListRework>;
};

export function RepaidLiabilitiesEditForm(props: Props) {
  const [loanProviderListQuery] = useQuery(loanProviders);
  const formatMessage = useFormatMessage();

  return pipe(
    loanProviderListQuery,
    remoteData.fold(
      () => (
        <Loader
          ariaLabelMessage={formatMessage(
            "generic.loading.alt" as RuntimeLocaleKey
          )}
        />
      ),
      () => <ErrorBanner>{formatMessage("GenericError")}</ErrorBanner>,
      loanProviders => (
        <RepaidLiabilitiesEditFormInternal
          {...props}
          loanProviderList={loanProviders.loanProviderList}
        />
      )
    )
  );
}

function RepaidLiabilitiesEditFormInternal(
  props: Props & {
    loanProviderList: NonEmptyArray<{
      code: NonEmptyString;
      name: NonEmptyString;
    }>;
  }
) {
  const formatMessage = useFormatMessage();
  const { nonEmptyString } = useValidators();
  const isMobileLayout = useIsMobileLayout();

  const loanProviderSelectedCommand = useCommand(loanProviderSelected);
  const {
    apiParameters: { tenant },
  } = useAppContext();

  const defaultCreditorConfig: CreditorConfig = foldTenant(
    tenant,
    () => ({
      accountPlaceholder:
        "StandardLoan.AccountsForRefinancing.accountForDisbursmentPlaceholder",
      variableSymbolPlaceholder: option.some(
        "StandardLoan.AccountsForRefinancing.vsDefaultPlaceholder"
      ),
      accountForDisbursment: option.none,
      accountReadonly: false,
    }),
    () => ({
      accountPlaceholder:
        "StandardLoan.AccountsForRefinancing.accountForDisbursmentPlaceholder",
      variableSymbolPlaceholder: option.some(
        "StandardLoan.AccountsForRefinancing.vsDefaultPlaceholder"
      ),
      accountForDisbursment: option.none,
      accountReadonly: false,
    })
  );

  const [creditorConfigs, setCreditorConfigs] = useState<CreditorConfig[]>(
    Array(props.liabilities.length).fill({ ...defaultCreditorConfig })
  );

  // This dummy validator is to avoid a known error in useForm fieldArrayValidator
  const workAroundAlwaysRightValidator = <T extends any>() =>
    validators.fromPredicate<T>(constTrue, formatMessage("GenericError"));

  const { fieldArray, handleSubmit } = useForm(
    {
      initialValues: {
        externalLiabilities: props.liabilities.map(
          liability =>
            ({
              ...liability,
              creditorName: pipe(
                liability.creditor,
                option.getOrElse(() => "")
              ),
            } as RepaidLiabilityFormModel)
        ),
      },
      fieldValidators: () => ({}),
      fieldArrayValidators: () => ({
        externalLiabilities: {
          creditorName: validators.inSequence(
            nonEmptyString,
            validators.fromPredicate(
              text =>
                props.loanProviderList.filter(lp => lp.name === text).length >
                0,
              formatMessage("StandardLoan.AccountsForRefinancing.wrongBankName")
            )
          ),
          recordId: workAroundAlwaysRightValidator<NonEmptyString>(),
          originalAmount: workAroundAlwaysRightValidator<MoneyAmount>(),
          type: workAroundAlwaysRightValidator<LoanType>(),
        },
      }),
    },
    {
      onSubmit: values =>
        pipe(
          {
            liabilitiesList: values.externalLiabilities.map(liability => ({
              type: liability.type,
              recordId: liability.recordId,
              creditor: option.some(liability.creditorName),
              creditorCode: pipe(
                props.loanProviderList,
                array.findFirstMap(lp =>
                  lp.name === liability.creditorName
                    ? option.some(lp.code)
                    : option.none
                )
              ),
            })),
          },
          props.onSubmit
        ),
    }
  );

  const submitButton = (
    <Box
      column={isMobileLayout}
      {...(!isMobileLayout && { hAlignContent: "right" })}
      style={
        isMobileLayout
          ? { width: "100%", paddingRight: "10px", paddingLeft: "10px" }
          : undefined
      }
    >
      <LoadingButton
        type="submit"
        variant="primary"
        size="default"
        labels={{
          normal: formatMessage("StandardLoan.RepaidLoans.button"),
          success: formatMessage("StandardLoan.RepaidLoans.button"),
          loading: formatMessage("Loading"),
          error: formatMessage("Error"),
        }}
        action={handleSubmit}
      />
    </Box>
  );

  const externalLiabilitiesFieldArray = fieldArray("externalLiabilities");

  const setCreditorConfigByIndex = (
    creditorConfig: CreditorConfig,
    index: number
  ) => {
    const newConfig = { ...creditorConfigs };
    newConfig[index] = creditorConfig;
    setCreditorConfigs(newConfig);
  };

  const updateCreditorConfig = (
    creditor: NonEmptyString,
    loanType: LoanType,
    index: number
  ) =>
    foldTenant(tenant, constVoid, () =>
      pipe(
        loanProviderSelectedCommand({
          creditor,
          type: loanType,
        }),
        taskEither.fold(
          () =>
            taskEither.fromIO(() =>
              setCreditorConfigByIndex(defaultCreditorConfig, index)
            ),
          provider =>
            taskEither.fromIO(() =>
              pipe(
                provider,
                option.fold(
                  () => setCreditorConfigByIndex(defaultCreditorConfig, index),
                  provider =>
                    setCreditorConfigByIndex(
                      {
                        accountPlaceholder: provider.accountForDisbursmentHint,
                        variableSymbolPlaceholder: provider.variableSymbolHint,
                        accountForDisbursment: provider.accountForDisbursment,
                        accountReadonly: provider.accountReadonly,
                      },
                      index
                    )
                )
              )
            )
        )
      )()
    );

  return (
    <Stack column units={4}>
      {externalLiabilitiesFieldArray.items.map(
        ({ fieldProps, namePrefix }, index) => (
          <RepaidLiabilitiesEdititableItem
            fieldProps={fieldProps}
            namePrefix={namePrefix}
            loanProviders={props.loanProviderList}
            onCreditorNameChanged={creditorCode =>
              updateCreditorConfig(
                creditorCode,
                fieldProps("type").value,
                index
              )
            }
            creditorConfig={creditorConfigs[index]}
            oldValues={pipe(
              props.oldValues,
              option.chain(oldValues => {
                // Sometimes we receive an undefined value that cause
                // problems to the ExternalLiabilitiesEdititableItem component
                const value = oldValues.liabilitiesList[index];
                if (value !== undefined) {
                  return option.some(value);
                } else {
                  return option.none;
                }
              })
            )}
          />
        )
      )}

      <Space units={10} />
      <Box
        column={isMobileLayout}
        hAlignContent={isMobileLayout ? "center" : "right"}
      >
        {submitButton}
      </Box>
    </Stack>
  );
}
